/**
 * A courier/partner-email order, styled to match OrderCard.jsx so it sits
 * naturally in the same grid — same card frame (OpsUi OrderCardShell), same
 * header/customer/items/total layout. Two real differences on
 * purpose: an "Email" source tag (this did not come in through rushmyfood's own
 * checkout) and no action buttons on the card itself. First Delivery drives a
 * DELIVERY order's status automatically, so there is nothing here for a
 * restaurant to Accept or Mark Ready. A non-delivery order (pickup/dine-in) is
 * the one exception — its lifecycle has no such automatic driver, so tapping
 * through to EmailOrderDetail offers Mark Picked Up there, for the
 * restaurant's own accounting.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import EmailSourceChip from './EmailSourceChip'
import { ops, FS, tabular } from './opsTokens'
import {
  OrderCardShell, CardBody, CardHeader, CardFooter, StatusChip, FulfilmentTag, CustomerLine,
  ItemsLine, Callout, DriverStrip, InitialsAvatar, VehicleIcon
} from './OpsUi'

/* Tones follow the Orders page's one status map (opsTokens.js):
   draft/scheduled slate, new amber, in hand blue, ready violet, out sky,
   delivered emerald, cancelled rose. */
export const COURIER_STATUS_CONFIG = {
  // Not yet imported into an order at all — still just the parsed email.
  RECEIVED:     { label: 'Received',        tone: 'amber' },
  PARSED:       { label: 'Parsed',          tone: 'amber' },
  DRAFT:        { label: 'Draft',           tone: 'slate' },
  NEEDS_REVIEW: { label: 'Needs Review',    tone: 'amber' },
  READY:        { label: 'Ready',           tone: 'violet' },
  SCHEDULED:    { label: 'Scheduled',       tone: 'slate' },
  DISPATCHING:  { label: 'Dispatching',     tone: 'blue' },
  ASSIGNED:     { label: 'Driver Assigned', tone: 'sky' },
  PICKED_UP:    { label: 'Picked Up',       tone: 'sky' },
  EN_ROUTE:     { label: 'On The Way',      tone: 'sky' },
  DELIVERED:    { label: 'Delivered',       tone: 'emerald' },
  CANCELLED:    { label: 'Cancelled',       tone: 'rose' },
  FAILED:       { label: 'Failed',          tone: 'rose' },
}

/* A promise time this far past when the order reached us means the customer
   asked for it later, not as soon as possible.

   The parser does decide this, but it keeps the verdict on the email row's
   validation blob — it is not a column on the order. Nothing sets the SCHEDULED
   status either; a pre-order rests in READY with a future dispatchAt. So the
   gap is the only signal an order itself carries. Same window as the eHungry
   mirror: past a normal kitchen quote, short of a real pre-order. */
export const COURIER_ASAP_WINDOW_MS = 45 * 60 * 1000
/* How long before the food is due the kitchen should pick it up — and, on the
   tablet, when its ticket prints. The two use the same moment so an order joins
   the work list exactly when its paper appears. */
export const COURIER_PRINT_LEAD_MS = 45 * 60 * 1000

export const isScheduledCourierOrder = (order) =>
  !!order && !!order.promiseTime && !!order.createdAt &&
  new Date(order.promiseTime).getTime() - new Date(order.createdAt).getTime() > COURIER_ASAP_WINDOW_MS

/** When a pre-order stops being "for later" and becomes work in hand. */
export const courierPrintDueAt = (order) =>
  isScheduledCourierOrder(order)
    ? new Date(order.promiseTime).getTime() - COURIER_PRINT_LEAD_MS
    : new Date(order.createdAt).getTime()

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`
  return new Date(dateStr).toLocaleDateString()
}

const EmailOrderCard = ({ order, onViewDetail, currencySymbol = '$' }) => {
  const config = COURIER_STATUS_CONFIG[order.status] || { label: order.status, tone: 'neutral' }

  const items = order.items || []
  const itemsSummary = items
    .slice(0, 2)
    .map((i) => `${i.quantity}x ${i.title}`)
    .join(', ')
  const moreCount = items.length - 2

  return (
    <OrderCardShell
      toneName={config.tone}
      label={`Email order #${order.externalOrderNumber || order._id}, ${config.label}`}
      onOpen={() => onViewDetail && onViewDetail(order)}
    >
      <CardBody>
        <CardHeader
          id={`#${order.externalOrderNumber || order._id}`}
          time={
            <>
              <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
              <Typography component="span" sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
                {timeAgo(order.createdAt)}
              </Typography>
            </>
          }
          right={<StatusChip toneName={config.tone} label={config.label} />}
        />

        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" minWidth={0}>
          <EmailSourceChip provider={order.merchantProvider} label="Email" />
          <FulfilmentTag kind={order.orderKind === 'DELIVERY' ? 'delivery' : 'pickup'}>
            {order.orderKind === 'DELIVERY' ? 'Delivery' : (order.orderKind || 'Unknown')}
          </FulfilmentTag>
          {/* Same place and style as the RMF (old) card's restaurant name. */}
          {order.merchantName && (
            <Typography noWrap sx={{ fontSize: FS.sm, color: ops.text2, fontWeight: 500, minWidth: 0 }}>
              {order.merchantName}
            </Typography>
          )}
        </Box>

        <CustomerLine name={order.customerName} phone={order.customerPhone} />

        <ItemsLine text={itemsSummary} more={moreCount} />
      </CardBody>

      <CardFooter
        left={
          <Typography sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
            {items.length} item{items.length === 1 ? '' : 's'}
          </Typography>
        }
        total={order.total != null ? `${currencySymbol}${Number(order.total).toFixed(2)}` : '—'}
      >
        {/* Only the explicitly-unpaid case gets a flag on the card itself — that's
            the one that's actionable at a glance (collect payment). Paid and
            unknown both stay quiet here; the full Payment section in the detail
            view is where those get spelled out. */}
        {order.isPaid === false && (
          <Callout toneName="rose" icon={<WarningAmberRoundedIcon />}>
            Unpaid
          </Callout>
        )}

        {order.fdDriverName && (
          <DriverStrip sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <InitialsAvatar name={order.fdDriverName} size={26} />
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: FS.sm, fontWeight: 600, color: ops.text }}>
              {order.fdDriverName}
            </Typography>
            <VehicleIcon transport={order.fdDriverTransport} />
          </DriverStrip>
        )}
      </CardFooter>
    </OrderCardShell>
  )
}

export default EmailOrderCard
